/**
 * Утилиты для преобразования между charIndex (видимый индекс) и сырыми позициями символов.
 * Мягкие переносы (\u00AD) присутствуют в DOM, но не считаются видимыми.
 */

import { SOFT_HYPHEN, stripSoftHyphens } from "./text-content";

/**
 * Преобразует charIndex в сырой индекс (с учётом мягких переносов).
 */
export function charIndexToRawOffset(text: string, charIndex: number): number {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (count === charIndex) {
      return i;
    }
    if (text[i] !== SOFT_HYPHEN) {
      count++;
    }
  }
  return text.length;
}

/**
 * Преобразует сырой индекс символа в charIndex (без мягких переносов).
 */
export function rawOffsetToCharIndex(text: string, rawIndex: number): number {
  const textBefore = text.slice(0, rawIndex);
  return stripSoftHyphens(textBefore).length;
}

/**
 * Возвращает все текстовые узлы внутри элемента.
 */
export function getTextNodesInElement(element: Element): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    textNodes.push(node);
  }

  return textNodes;
}

/**
 * Находит DOM-позицию (текстовый узел + смещение) по charIndex.
 */
export function charIndexToDomPosition(
  element: Element,
  charIndex: number,
  cachedTextNodes?: Text[]
): { node: Text; offset: number } | null {
  const textNodes = cachedTextNodes || getTextNodesInElement(element);
  let currentCount = 0;

  for (let i = 0; i < textNodes.length; i++) {
    const textNode = textNodes[i];
    const text = textNode.textContent || "";
    const visibleLength = stripSoftHyphens(text).length;

    if (currentCount + visibleLength > charIndex) {
      const offsetInNode = charIndex - currentCount;
      const rawOffset = charIndexToRawOffset(text, offsetInNode);
      return { node: textNode, offset: rawOffset };
    }

    currentCount += visibleLength;
  }

  // charIndex === totalVisibleLength — позиция в конце элемента
  const lastTextNode = textNodes[textNodes.length - 1];
  if (lastTextNode && charIndex === currentCount) {
    return { node: lastTextNode, offset: lastTextNode.length };
  }

  return null;
}

/**
 * Вычисляет charIndex по DOM-позиции (текстовый узел + смещение).
 */
export function domPositionToCharIndex(
  element: Element,
  targetNode: Text,
  offset: number
): number {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  let charIndex = 0;

  let textNode = walker.nextNode() as Text | null;
  while (textNode) {
    if (textNode === targetNode) {
      const textBeforeOffset = (textNode.textContent || "").slice(0, offset);
      charIndex += stripSoftHyphens(textBeforeOffset).length;
      break;
    }
    const text = textNode.textContent || "";
    charIndex += stripSoftHyphens(text).length;
    textNode = walker.nextNode() as Text | null;
  }

  return charIndex;
}
